<template>
  <b-modal
    id="dtc-import-modal"
    ref="bModal"
    :title="$t('Importar Códigos DTC')"
    cancel-variant="outline-secondary"
    hide-footer
    @hidden="resetModal"
  >
    <div class="mb-2 text-center">
      <b-button variant="flat-primary" @click="downloadTemplate" class="mb-1">
        <feather-icon icon="DownloadIcon" class="mr-50" />
        {{ $t('Baixar template de importação') }}
      </b-button>
    </div>

    <b-form-group>
      <div
        class="dropzone border-primary p-3 text-center rounded d-flex flex-column align-items-center justify-content-center"
        @dragover.prevent="dragover"
        @dragleave.prevent="dragleave"
        @drop.prevent="drop"
        :class="{ 'bg-light-primary': isDragging }"
        style="border: 2px dashed; cursor: pointer; min-height: 150px;"
        @click="$refs.fileInput.click()"
      >
        <feather-icon icon="UploadCloudIcon" size="3x" class="text-primary mb-1" />
        <h5 v-if="!selectedFile">{{ $t('Arraste o arquivo ou clique para selecionar') }}</h5>
        <h5 v-else class="text-success">{{ selectedFile.name }}</h5>
        <small class="text-muted">{{ $t('Formatos aceitos: .xlsx, .csv') }}</small>
        
        <input
          type="file"
          ref="fileInput"
          class="d-none"
          accept=".xlsx, .csv"
          @change="onFileSelected"
        >
      </div>
    </b-form-group>

    <div class="d-flex justify-content-end mt-2">
      <b-button variant="outline-secondary" @click="$refs.bModal.hide()" class="mr-1">
        {{ $t('Cancelar') }}
      </b-button>
      <b-button
        variant="primary"
        @click="uploadFile"
        :disabled="!selectedFile || isUploading"
      >
        <b-spinner v-if="isUploading" small class="mr-50" />
        {{ $t('Importar') }}
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { BModal, BButton, BFormGroup, BSpinner } from 'bootstrap-vue'
import store from '@/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from '@axios'

export default {
  components: {
    BModal, BButton, BFormGroup, BSpinner
  },
  props: {
    dtcTableId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      selectedFile: null,
      isDragging: false,
      isUploading: false
    }
  },
  methods: {
    show() {
      this.$refs.bModal.show()
    },
    dragover() {
      this.isDragging = true
    },
    dragleave() {
      this.isDragging = false
    },
    drop(e) {
      this.isDragging = false
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        this.selectedFile = e.dataTransfer.files[0]
      }
    },
    onFileSelected(e) {
      if (e.target.files && e.target.files.length > 0) {
        this.selectedFile = e.target.files[0]
      }
    },
    downloadTemplate() {
      this.$swal({
        title: this.$t('Atenção ao Cabeçalho'),
        text: this.$t('Você não pode editar nem retirar o cabeçalho da planilha modelo, pois isso impedirá o sistema de reconhecer as colunas corretamente durante a importação.'),
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: this.$t('Entendi, Baixar Planilha'),
        cancelButtonText: this.$t('Cancelar'),
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-danger ml-1' },
      }).then(result => {
        if (result.value) {
          // Usando axios direto para baixar o blob, como é de praxe
          axios.get('/api/v1/DTC/table/items/import/model', { responseType: 'blob' })
            .then(response => {
              const url = window.URL.createObjectURL(new Blob([response.data]))
              const link = document.createElement('a')
              link.href = url
              link.setAttribute('download', 'Modelo_Importacao_DTC.xlsx')
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
            })
            .catch(() => {
              this.$toast({
                component: ToastificationContent,
                props: { title: this.$t('Erro ao baixar template'), icon: 'AlertTriangleIcon', variant: 'danger' }
              })
            })
        }
      })
    },
    uploadFile() {
      if (!this.selectedFile) return

      this.isUploading = true
      store.dispatch('app-dtc/importItems', { dtcTableId: this.dtcTableId, file: this.selectedFile })
        .then(response => {
          const res = response.data || {}
          
          if (res.errors && res.errors.length > 0) {
            let errorListHtml = '<ul style="text-align: left; max-height: 200px; overflow-y: auto; padding-left: 20px; font-size: 0.9rem;">'
            res.errors.forEach(err => {
              errorListHtml += `<li>${err}</li>`
            })
            errorListHtml += '</ul>'

            this.$swal({
              title: this.$t('Importação com ressalvas'),
              html: `
                <p class="mb-1">${this.$t('Linhas inseridas com sucesso:')} <strong>${res.inserted || 0}</strong></p>
                <p class="mb-1 text-danger">${this.$t('Linhas ignoradas ou com erro:')} <strong>${res.skipped || 0}</strong></p>
                <hr>
                <p class="mb-1 text-left"><strong>${this.$t('Detalhes dos erros:')}</strong></p>
                ${errorListHtml}
              `,
              icon: 'warning',
              customClass: { confirmButton: 'btn btn-warning' }
            })
          } else {
            this.$swal({
              title: this.$t('Importação concluída'),
              text: this.$t(`Foram importados ${res.inserted || 0} códigos DTC com sucesso!`),
              icon: 'success',
              customClass: { confirmButton: 'btn btn-success' }
            })
          }
          
          this.$emit('imported')
          this.$refs.bModal.hide()
        })
        .catch(error => {
          const errorMessage = error.response?.data?.message || this.$t('Ocorreu um erro inesperado ao processar a planilha. Verifique o formato do arquivo e tente novamente.')
          
          this.$swal({
            title: this.$t('Falha na Importação'),
            text: errorMessage,
            icon: 'error',
            customClass: { confirmButton: 'btn btn-primary' }
          })
        })
        .finally(() => {
          this.isUploading = false
        })
    },
    resetModal() {
      this.selectedFile = null
      this.isDragging = false
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    }
  }
}
</script>

<style scoped>
</style>
